import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { genres, subgenres, tropes, spiceLevels, darknessLevels, endingTypes, contentWarnings } from '../data/bookTags';
import { darknessLevelToBrowseSlug, spiceLevelToBrowseSlug, toSlug } from '../utils/filterVocabulary';

/**
 * READING PREFERENCES — the shared, persisted store.
 *
 * The reader's settings page already owned exactly these fields in local
 * state; they were simply lost on navigation and invisible to the rest of the
 * app. This context lifts THOSE SAME FIELDS into one persisted place so
 * discovery can read them. No new preference concepts are introduced.
 *
 * The allowlist/blocklist design is deliberately kept as-is: the settings UI
 * stores human-readable names ('Fantasy', 'Spicy', 'HEA') in two flat arrays,
 * spanning every category. Rather than restructure that, this module CLASSIFIES
 * each saved tag back into the bucket Browse needs by looking it up in the
 * canonical tag vocabularies.
 */

export interface ReadingPreferences {
  burnLevel: string;
  format: string;
  comfortLevel: string;
  allowlistEnabled: boolean;
  allowlistTags: string[];
  blocklistTags: string[];
  discoverySaveDestination: string;
}

/** Preferences translated into the slug vocabulary Browse's filters use. */
export interface PreferenceFilters {
  genres: string[];
  subgenres: string[];
  tropes: string[];
  spiceLevels: string[];
  darknessLevels: string[];
  endingTypes: string[];
  excludedWarnings: string[];
}
export const EMPTY_PREFERENCE_FILTERS: PreferenceFilters = {
  genres: [],
  subgenres: [],
  tropes: [],
  spiceLevels: [],
  darknessLevels: [],
  endingTypes: [],
  excludedWarnings: []
};

/**
 * Seeded to match the settings page's existing defaults, so the prototype
 * demonstrates the sync immediately rather than requiring a save first.
 * 'Second Chance' and 'HEA' are what make Ember and Ash's Light Edition the
 * preference-driven match.
 */
const DEFAULT_PREFERENCES: ReadingPreferences = {
  burnLevel: 'Medium to Slow',
  format: 'eBook, Paperback',
  comfortLevel: 'Medium Spice',
  allowlistEnabled: true,
  allowlistTags: ['Romance', 'HEA'],
  blocklistTags: ['Graphic Violence', 'Animal cruelty'],
  discoverySaveDestination: 'wishlist'
};
const STORAGE_KEY = 'litlaunch_reading_preferences';

// ── Tag classification ───────────────────────────────────────────────
// Built once from the canonical vocabularies. A saved tag is matched by slug,
// so casing and punctuation differences between surfaces don't break the sync.

const buildLookup = <T extends {
  id: string;
  name: string;
},>(entries: T[]): Map<string, T> => {
  const map = new Map<string, T>();
  entries.forEach(entry => {
    map.set(toSlug(entry.name), entry);
    map.set(toSlug(entry.id), entry);
  });
  return map;
};
const GENRE_LOOKUP = buildLookup(genres);
const SUBGENRE_LOOKUP = buildLookup(subgenres as any);
const TROPE_LOOKUP = buildLookup(tropes as any);
const SPICE_LOOKUP = buildLookup(spiceLevels as any);
const DARKNESS_LOOKUP = buildLookup(darknessLevels as any);
const ENDING_LOOKUP = buildLookup(endingTypes as any);
const WARNING_LOOKUP = buildLookup(contentWarnings as any);

/**
 * Translates saved preference tags into Browse's filter slugs.
 *
 * Allowlist tags become positive filter selections; blocklist tags become
 * content-warning exclusions. A blocklist entry that isn't a known warning is
 * still passed through as an exclusion — readers block things the canonical
 * vocabulary hasn't catalogued yet, and dropping them silently would be worse
 * than matching them by name.
 */
export function preferencesToFilters(preferences: ReadingPreferences): PreferenceFilters {
  const result: PreferenceFilters = {
    genres: [],
    subgenres: [],
    tropes: [],
    spiceLevels: [],
    darknessLevels: [],
    endingTypes: [],
    excludedWarnings: []
  };

  // The existing allowlist toggle governs whether positive preferences are
  // enforced, so it keeps its meaning here rather than being bypassed.
  // Blocklist exclusions always apply — a reader blocking content means it,
  // toggle or not.
  const allowlist = preferences.allowlistEnabled ? preferences.allowlistTags : [];
  allowlist.forEach(tag => {
    const slug = toSlug(tag);
    if (GENRE_LOOKUP.has(slug)) {
      const genre = GENRE_LOOKUP.get(slug)!;
      result.genres.push(toSlug(genre.id));
      return;
    }
    if (SUBGENRE_LOOKUP.has(slug)) {
      result.subgenres.push(slug);
      return;
    }
    if (SPICE_LOOKUP.has(slug)) {
      const level = (SPICE_LOOKUP.get(slug) as any).level as number;
      const browseSlug = spiceLevelToBrowseSlug(level);
      if (browseSlug) result.spiceLevels.push(browseSlug);
      return;
    }
    if (DARKNESS_LOOKUP.has(slug)) {
      const level = (DARKNESS_LOOKUP.get(slug) as any).level as number;
      const browseSlug = darknessLevelToBrowseSlug(level);
      if (browseSlug) result.darknessLevels.push(browseSlug);
      return;
    }
    if (ENDING_LOOKUP.has(slug)) {
      result.endingTypes.push(toSlug((ENDING_LOOKUP.get(slug) as any).id));
      return;
    }
    if (TROPE_LOOKUP.has(slug)) {
      result.tropes.push(slug);
      return;
    }
    // Unrecognised allowlist tags are treated as tropes, which is where the
    // settings UI's free-text entries most often belong.
    result.tropes.push(slug);
  });
  preferences.blocklistTags.forEach(tag => {
    const slug = toSlug(tag);
    const warning = WARNING_LOOKUP.get(slug);
    result.excludedWarnings.push(warning ? warning.name : tag);
  });
  return result;
}
interface ReadingPreferencesContextValue {
  /** The saved preferences. Only changes when the reader saves in Settings. */
  preferences: ReadingPreferences;
  /** Saved preferences expressed in Browse's filter vocabulary. */
  preferenceFilters: PreferenceFilters;
  /** True when the reader has any saved preference to restore. */
  hasPreferences: boolean;
  /** Explicit save from the Settings page. The ONLY way preferences change. */
  savePreferences: (next: Partial<ReadingPreferences>) => void;
}
const ReadingPreferencesContext = createContext<ReadingPreferencesContextValue | undefined>(undefined);
export const ReadingPreferencesProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [preferences, setPreferences] = useState<ReadingPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return {
        ...DEFAULT_PREFERENCES,
        ...JSON.parse(stored)
      };
    } catch {
      // Ignore malformed storage and fall back to defaults.
    }
    return DEFAULT_PREFERENCES;
  });
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Storage unavailable; preferences stay in memory for this session.
    }
  }, [preferences]);
  const value = useMemo<ReadingPreferencesContextValue>(() => {
    const preferenceFilters = preferencesToFilters(preferences);
    const hasPreferences = Object.values(preferenceFilters).some(list => list.length > 0);
    return {
      preferences,
      preferenceFilters,
      hasPreferences,
      // Merges rather than replaces, so a settings section can save its own
      // fields without clobbering the rest.
      savePreferences: next => setPreferences(prev => ({
        ...prev,
        ...next
      }))
    };
  }, [preferences]);
  return <ReadingPreferencesContext.Provider value={value} data-id="element-4124" data-mp="3mea0bgek63n6">
      {children}
    </ReadingPreferencesContext.Provider>;
};
export const useReadingPreferences = (): ReadingPreferencesContextValue => {
  const context = useContext(ReadingPreferencesContext);
  if (!context) {
    // Keeps surfaces usable outside the provider (e.g. isolated previews).
    return {
      preferences: DEFAULT_PREFERENCES,
      preferenceFilters: EMPTY_PREFERENCE_FILTERS,
      hasPreferences: false,
      savePreferences: () => {}
    };
  }
  return context;
};